/**
 * Double Shift Detector
 * 
 * Utility for detecting double key presses (specifically double shift).
 * Can be extended for other double-press patterns.
 */

import { TIMING } from '../shortcuts/shortcutConfig';

/**
 * Class for detecting double key presses
 */
export class DoubleKeyDetector {
  constructor(key, timeWindow = TIMING.DOUBLE_SHIFT_TIME) {
    this.key = key;
    this.timeWindow = timeWindow;
    this.lastKeyTime = 0;
    this.onDoublePress = null;
  }
  
  /**
   * Set the callback for double press events
   */
  setCallback(callback) {
    this.onDoublePress = callback;
  }
  
  /**
   * Handle key press event
   */
  handleKeyPress(event) {
    if (event.key !== this.key) {
      return false;
    }
    
    // Skip if modifiers are pressed (we want clean double presses)
    if (event.ctrlKey || event.altKey || event.metaKey) {
      return false;
    }
    
    const currentTime = Date.now();
    const timeSinceLastPress = currentTime - this.lastKeyTime;
    
    if (timeSinceLastPress < this.timeWindow && timeSinceLastPress > 0) {
      // Double press detected
      this.lastKeyTime = 0; // Reset to prevent triple press
      if (this.onDoublePress) {
        this.onDoublePress(event);
      }
      return true;
    } else {
      // First press or too slow
      this.lastKeyTime = currentTime;
      return false;
    }
  }
  
  /**
   * Reset the detector state
   */
  reset() {
    this.lastKeyTime = 0;
  }
}

/**
 * Create a double shift detector instance
 */
export const createDoubleShiftDetector = () => {
  return new DoubleKeyDetector('Shift');
};

/**
 * Hook for using double shift detection
 */
export const useDoubleShiftDetector = (callback) => {
  const detector = new DoubleKeyDetector('Shift');
  detector.setCallback(callback);
  return detector;
};